import { useState } from 'react';
import { z } from 'zod';
import { usePlatformPrivilege } from '@mews-commander/core';
import { useRepositoryQuery } from '@mews-commander/data-access';
import { ArticlePath, createGuideLink, TextKey } from '@mews-commander/enums';
import { LocalizedParagraph, LocalizedTypography, NewTabLink } from '@mews-commander/ui';
import { LocalizedText, useDialog, useLocalStorageSafe } from '@mews/framework';
import { Banner, Button, Card, IconButton, IconName, Stack } from '@optimus-web/core';
import { SecurityCardSkeleton } from '../components/SecurityCardSkeleton';
import { StatusTag } from '../components/StatusTag';
import { useOwnerIdQueryPayload } from '../queries/useOwnerId';
import { ExceptionsDialog } from './ExceptionsDialog';

const schema = z.boolean().optional();

export const Authentication = () => {
    const [{ active, onClose }, toggleDialog] = useDialog();
    const ownerIdPayload = useOwnerIdQueryPayload();
    const { data, isLoading } = useRepositoryQuery('mfaConfiguration', 'get', ownerIdPayload);

    const canAccessAllEnterprises = usePlatformPrivilege('accessAllEnterprises');
    const isPlatformAdmin = usePlatformPrivilege('admin');

    const isPrivilegedPlatformUser = canAccessAllEnterprises || isPlatformAdmin;

    const hide2FABannerStorage = useLocalStorageSafe('hide2FABanner', schema);
    const [bannerHidden, setBannerHidden] = useState(hide2FABannerStorage.getSafe());

    const hideBanner = () => {
        setBannerHidden(true);
        hide2FABannerStorage.set(true);
    };

    if (isLoading) {
        return <SecurityCardSkeleton />;
    }

    return (
        <>
            <Stack vertical spacing="300">
                {data?.enforceAuthenticatorSetup && !bannerHidden && (
                    <Banner
                        variant="info"
                        primaryText={<LocalizedText textKey={TextKey.TwoFactorAuthenticationInfoTitle} />}
                        secondaryText={<LocalizedText textKey={TextKey.TwoFactorAuthenticationInfoDescription} />}
                        action={
                            <NewTabLink url="https://community.mews.com/p/2FA" variant="basic" inherit={false}>
                                <LocalizedText textKey={TextKey.TwoFactorAuthenticationInfoButton} />
                            </NewTabLink>
                        }
                        onClose={hideBanner}
                    />
                )}
                <Card padding="200">
                    <Stack vertical spacing="200">
                        <Stack vertical spacing="100">
                            <Stack distribution="space-between" spacing="100" itemAlignment="center">
                                <LocalizedTypography
                                    textKey={TextKey.TwoFactorAuthenticationCardTitle}
                                    textStyle="titleSmallStrong"
                                    as="div"
                                />
                                <StatusTag isEnabled={data?.enforceAuthenticatorSetup} />
                            </Stack>

                            <LocalizedParagraph align="left" textKey={TextKey.TwoFactorAuthenticationCardDescription} />
                        </Stack>
                        {(data?.enforceAuthenticatorSetup || isPrivilegedPlatformUser) && (
                            <Stack spacing="200" distribution="space-between">
                                <Button variant="tertiary" size="small" onClick={toggleDialog} type="button">
                                    <LocalizedText
                                        textKey={
                                            data?.enforceAuthenticatorSetup
                                                ? TextKey.TwoFactorAuthenticationCardButton
                                                : TextKey.ShowDetails
                                        }
                                    />
                                </Button>
                                <NewTabLink url={createGuideLink(ArticlePath.MfaExceptions)}>
                                    <IconButton variant="ghost" size="small" icon={IconName.Help} type="button" />
                                </NewTabLink>
                            </Stack>
                        )}
                    </Stack>
                </Card>
            </Stack>
            {data ? <ExceptionsDialog active={active} onClose={onClose} data={data} /> : null}
        </>
    );
};
